// Search results page (InstantSearch).

import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {
  configure,
  hits,
  refinementList,
  menuSelect,
  pagination,
  sortBy,
  stats,
  clearRefinements,
  searchBox,
} from 'instantsearch.js/es/widgets';

import '../../scss/search.scss';

import config, { contentTypeLabel, visibleFacets } from '../config.js';
import { buildKicker } from '../kicker.js';

/** Read the `keywords` query param used to bootstrap the search on load. */
function getSearchPageKeyword() {
  const params = new URLSearchParams(window.location.search);
  return params.get('keywords') || '';
}

/** Wire the mobile "Filters" toggle (collapsed by default under the breakpoint). */
function initFiltersToggle() {
  const toggle = document.querySelector('.search-page__filters-toggle');
  const aside = document.getElementById('search-filters');
  if (!toggle || !aside) {
    return;
  }
  toggle.addEventListener('click', () => {
    const open = aside.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
}

export default function algoliaSearchPage() {
  const searchPage = document.getElementById('search-page-container');
  if (!searchPage || !config.applicationId) {
    return;
  }

  const searchClient = algoliasearch(config.applicationId, config.searchApiKey);

  let searchPageOnLoad = true;

  const search = instantsearch({
    indexName: config.indexName,
    searchClient,
    searchFunction(helper) {
      if (searchPageOnLoad) {
        const keyword = getSearchPageKeyword();
        if (keyword) {
          helper.setQuery(keyword);
        }
        helper.search();
        searchPageOnLoad = false;
        return;
      }
      helper.search();
    },
  });

  const widgets = [
    configure({
      attributesToSnippet: ['content:65'],
      hitsPerPage: 20,
      filters: 'status:"publish"',
    }),
    searchBox({
      container: '#searchbox',
      searchAsYouType: false,
      showReset: false,
      showSubmit: true,
      placeholder: config.placeholder,
    }),
    // Content-type tab bar — data-driven (only content types that actually
    // have records appear). Disjunctive facet + fixed name sort so the bar
    // never reflows or reorders when a tab is toggled; selecting one only
    // recolors it.
    refinementList({
      container: '#content-type-tabs',
      attribute: 'content_type',
      limit: 20,
      sortBy: ['name:asc'],
      cssClasses: {
        list: 'content-type-tabs__list',
        item: 'content-type-tabs__item',
        selectedItem: 'content-type-tabs__item--selected',
        label: 'content-type-tabs__link',
        labelText: 'content-type-tabs__label',
        checkbox: 'content-type-tabs__checkbox',
        count: 'content-type-tabs__count',
      },
      transformItems(items) {
        // refinementList renders `highlighted`, so map both to the human label.
        return items.map((item) => {
          const label = contentTypeLabel(item.value);
          return { ...item, label, highlighted: label };
        });
      },
    }),
    hits({
      container: '#searchpageresults',
      cssClasses: {
        list: 'search-cards',
        item: 'search-cards__item',
      },
      templates: {
        item(hit, { html, components }) {
          return html`
            <article class="search-card">
              <h3 class="search-card__title">
                <a href="${hit.relative_url}" rel="bookmark">
                  ${components.Highlight({ hit, attribute: 'title' })}
                </a>
              </h3>
              <div class="search-card__meta">${buildKicker(hit, html)}</div>
              <p class="search-card__excerpt">
                ${components.Snippet({ hit, attribute: 'content' })}
              </p>
            </article>
          `;
        },
      },
    }),
    // Static "Most Recent" sort (single index; ranks desc(published_date)).
    sortBy({
      container: '#sort-by',
      items: [{ label: config.sortLabel, value: config.indexName }],
    }),
    clearRefinements({
      container: '#clear-refinements',
      cssClasses: { button: 'search-page__reset-button' },
      templates: { resetLabel: config.resetLabel },
    }),
    pagination({
      container: '#searchpagination',
      showFirst: true,
      showLast: true,
      scrollTo: true,
      templates: {
        first: 'First',
        previous: 'Previous',
        next: 'Next',
        last: 'Last',
      },
    }),
    stats({
      container: '#search_results_stats',
      templates: {
        text: `
          {{#hasNoResults}}No results{{/hasNoResults}}
          {{#hasOneResult}}<span class="num">1</span> result{{/hasOneResult}}
          {{#hasManyResults}}<span class="num">{{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}}</span> results{{/hasManyResults}}
        `,
      },
    }),
  ];

  // Sidebar dropdown filters (native <select> per facet), driven by the
  // localized facet config so unbacked/hidden facets are never rendered.
  visibleFacets().forEach((facet) => {
    const container = document.getElementById(`facet-${facet.attribute}`);
    if (!container) {
      return;
    }
    widgets.push(
      menuSelect({
        container: `#facet-${facet.attribute}`,
        attribute: facet.attribute,
        limit: 50,
        templates: {
          defaultOption: config.allLabel,
          item: '{{label}} ({{count}})',
        },
      })
    );
  });

  search.addWidgets(widgets);
  search.start();

  search.on('render', () => {
    const results = search.helper.lastResults;
    const noResults = results && results.nbHits === 0;
    ['search_page_results_section', 'search_page_no_results_section'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.classList.toggle('no-results', !!noResults);
      }
    });
  });

  initFiltersToggle();
}

algoliaSearchPage();
